import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ApiV2ReservationcellElFindParams } from '@/apis/swaggers/v2/v2-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { QueryComposableParams } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ApiV2ReservationcellElFindParams, unknown>

const fetchReservationCellList = async (params: ApiV2ReservationcellElFindParams, signal?: AbortSignal) => {
  const { data } = await apiClient.v2.apiV2ReservationcellElFind(params, { signal });
  return checkApiResponse(data);
}

export const v2ReservationCellKeys = {
  all: [QUERY_KEY.v2apiV2ReservationcellElFind] as const,
  list: (params: ApiV2ReservationcellElFindParams) => [...v2ReservationCellKeys.all, params] as const,
}

/** 명령형(await) 호출용 쿼리 정의.
 * queryFn 이 TanStack 의 AbortSignal 을 axios 로 전달하므로,
 * 진행 중 요청 취소는 `queryClient.cancelQueries({ queryKey: v2ReservationCellKeys.all })` 로 한다
 * (레거시 axios cancelToken 흐름의 대체) */
export const v2ReservationCellFindQueryOptions = (params: ApiV2ReservationcellElFindParams) => ({
  queryKey: v2ReservationCellKeys.list(params),
  queryFn: ({ signal }: { signal?: AbortSignal }) => fetchReservationCellList(params, signal),
  // 날짜별 데이터 — FCM invalidation(타 사용자 변경)과 mutation invalidation(내 변경)이 커버하므로 5분 캐시
  staleTime: 5 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
})

export const useV2ReservationCellFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: v2ReservationCellKeys.list(resolveMaybeGetter(params)),
    queryFn: ({ signal }) => fetchReservationCellList(resolveMaybeGetter(params), signal),
    ...resolveMaybeGetter(options ?? {}),
  }))
}
